import { TWidget } from 'widgets/Widget';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import {
    submitFormJson,
    getContentByUrl,
    submitFormJsonWithAbort,
    TControlledRequest,
    TFailedRequestResponse
} from 'widgets/toolbox/ajax';

import { showErrorLayout } from 'widgets/toolbox/util';
import { TModalInstance } from 'widgets/global/Modal';
import { TConfirmDialogInstance } from 'widgets/global/ConfirmDialog';
import { TInputStepperInstance } from 'widgets/forms/InputStepper';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TRadioSelectorInstance } from 'widgets/checkout/RadioSelector';
import { TShippingMethodModel } from 'types/models/order';
import { isDesktopView } from 'widgets/toolbox/viewtype';

export type TCart = {
    items: Array<Record<string, unknown>>;
    shipments: {
        selectedShippingMethod: string;
        shippingMethods: Array<TShippingMethodModel>;
    };
    valid: Record<string, unknown>;
    error: string;
    success: boolean;
    redirectUrl: string;
    couponAction?: string;
    couponCode?: string;
    pageError: string;
    showMinicart: boolean;
};

export type TAttributes = {
    'data-tau'?: string;
    url?: string;
    pid?: string;
    uuid?: string;
    currentValue?: number;
    minOrderQuantity?: number | null;
    optionId?: string;
    optionVal?: string;
    customOptionValue?: string;
    lineItemMinOrderQuantity?: number;
};

export type TProductAttributes = Record<string, string | number | boolean>;

export interface ICartInputStepperInstance extends TInputStepperInstance {
    prefs(): ReturnType<TInputStepperInstance['prefs']> & {
        lineItemMinOrderQuantity?: number;
    };
}

type TActionsUrls = {
    moveProductToWishlist: string;
    selectShippingMethod: string;
};

type TUpdateQtyRequest = TControlledRequest<TCart, { redirectUrl: string }>;

/**
 * @description Base CartMgr implementation
 * @param Widget Base widget for extending
 * @returns Cart Manager class
 */
function CartMgrClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class CartMgr
     * @augments Widget
     * @classdesc CartMgr component on Cart page. Renders the whole cart template on each update e.g. quantity/coupon/product edit etc.
     * Widget manages next actions:
     * 1. Edit product in a cart
     * 2. Change product quantity
     * 3. Remove product (move to Wish list) from Cart
     * 4. Communicate actions results
     * 5. Re-render Cart including products list and cart summary in case of actions like: product updated, coupon applied etc.
     * 6. Enable/disable "Secure Checkout" button to move to Checkout process depending on if there are errors in cart, which prevents moving to Checkout
     * @property {string} data-widget - Widget name `cartMgr`
     * @property {Array} data-items - Cart items list
     * @property {Array} data-approaching-discounts - Cart approaching discounts list
     * @property {object} data-action-urls - Cart action URLs
     * @property {object} data-totals - Cart totals
     * @property {Array} data-shipments - Cart shipments list
     * @property {boolean} data-valid - Is Cart valid
     * @property {number} data-num-items - Number of items in Cart
     * @property {string} data-cart-get-url - An URL to get updated Cart object from server
     * @property {object} data-accessibility-alerts - Accessibility alerts messages for different user actions
     * Possible values are: `quantitychanged`, `productupdated`, `productremoved`,
     * `promocodeadded`, `promocodeapplied`, `promocoderemoved`, `addedtowishlist`
     *
     * @example <caption>Example of CartMgr widget markup</caption>
     * <div
     *     data-widget="cartMgr"
     *     data-items="${JSON.stringify(pdict.items.toArray().concat(pdict.giftCertificateItems ? pdict.giftCertificateItems.toArray() : []))}"
     *     data-approaching-discounts="${JSON.stringify(pdict.approachingDiscounts ? pdict.approachingDiscounts.toArray() : [])}"
     *     data-action-urls="${JSON.stringify(pdict.actionUrls)}"
     *     data-cart-get-url="${URLUtils.url('Cart-Get')}"
     *     data-totals="${JSON.stringify(pdict.totals)}"
     *     data-shipments="${JSON.stringify(pdict.shipments ? pdict.shipments.toArray() : [])}"
     *     data-valid="${JSON.stringify(pdict.valid)}"
     *     data-num-items="${JSON.stringify(pdict.numItems)}"
     *     data-accessibility-alerts='{
     *         "quantitychanged": "${Resource.msg('alert.quantitychanged', 'cart', null)}",
     *         ...
     *     }'
     * >
     *     <div data-ref="cartContainer">
     *         <isprint value="${pdict.cartTemplateMustache}" encoding="off"/>
     *     </div>
     *     <noscript data-ref="cartTemplate" hidden="hidden">
     *          <isprint value="${pdict.cartTemplateISML}" encoding="off"/>
     *     </noscript>
     *
     *      <isinclude template="cart/cartRemoveProductModal"/>
     *      <isinclude template="cart/cartEditProductModal"/>
     *
     *      <script type="template/mustache" data-ref="errorTemplate">
     *          <div data-ref="errorMsgs">
     *              <div role="alert">
     *                  {{message}}
     *              </div>
     *          </div>
     *      </script>
     * </div>
     */
    class CartMgr extends Widget {
        removeButton?: TRefElementInstance | TInputStepperInstance;

        updateQtyRequests: Array<TUpdateQtyRequest> = [];

        prefs() {
            return {
                classesModalQuickView: 'm-quick_view',
                classesModalGiftCertificate: 'm-egift',
                cartGetUrl: '',
                accessibilityAlerts: <TAccessibilityAlerts>{},
                actionUrls: <TActionsUrls>{},
                tauAttrRemoveProductConfirmationPopUp: 'remove_item_confirmation_dialog',
                items: <TCart['items']> [],
                shipments: <TCart['shipments']> {},
                valid: <TCart['valid']> {},
                error: <TCart['error']> '',
                success: <TCart['success']> false,
                showMinicart: <TCart['showMinicart']> false,
                redirectUrl: <TCart['redirectUrl']> '',
                pageError: <TCart['pageError']> '',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         * @listens "minicart.updated"
         * @listens "product.updated"
         * @listens "coupon.updated"
         * @emits "cart.rendered"
         */
        init() {
            this.updateQtyRequests = [];
            this.eventBus().on('minicart.updated', 'renderCart');
            this.eventBus().on('product.updated', 'onProductUpdated');
            this.eventBus().on('coupon.updated', 'onCouponUpdated');
        }

        /**
         * @description Executes on product updated in Quick View
         * @param response Response object from server
         */
        onProductUpdated(response: TResponse & TCart | TResponse & unknown) {
            const PLI_EDITING_ACTION = 'Cart-EditProductLineItem';
            const GIFT_EDITING_ACTION = 'Cart-EditGiftCertificateLineItem';

            if (![PLI_EDITING_ACTION, GIFT_EDITING_ACTION].includes(response.action)) {
                return;
            }

            const cart = <TResponse & TCart>response;

            if (cart) {
                this.renderCart(cart)
                    .then(() => this.accessibilityAlert(this.prefs().accessibilityAlerts.productupdated));
            }
        }

        /**
         * @description Executes on product updated in Quick View
         * @param cart Cart object
         */
        onCouponUpdated(cart: TCart) {
            this.renderCart(cart)
                .then(() => {
                    if (cart) {
                        const accessibilityAlert = cart.couponCode + ' ' + this.prefs().accessibilityAlerts[`promocode${cart.couponAction}`];

                        this.accessibilityAlert(accessibilityAlert);
                    }
                });
        }

        /**
         * @description Renders Cart object and saves it as a current cart state
         * Adds some information into Cart object prior to rendering,
         * to make the whole object mustache template friendly
         * @emits "cart.updated"
         * @param cart - Cart object to reflect in template
         * @param updatePrefs - Update widget prefs according to new cart data
         * @returns Promise object represents cart rendering result
         */
        renderCart(cart: TCart, updatePrefs = true): Promise<void> {
            if (!cart) {
                return Promise.reject(new Error());
            }

            if (updatePrefs) {
                this.setPrefs(cart);
            }

            /**
             * @description Event, indicates that cart content was updated
             * @event "cart.updated"
             */
            this.eventBus().emit('cart.updated', cart);

            return this.render('cartTemplate', cart, this.ref('cartContainer'));
        }

        /**
         * @description Updates quantity of chosen product in Cart page
         * @param quantityStepper - Select quantity input
         * @returns Promise object represents server response with quantity updating result
         */
        updateQty(quantityStepper: ICartInputStepperInstance): Promise<null>| Promise<unknown> {
            this.showProgressBar();

            const url = quantityStepper.data<string>('action');
            const uuid = quantityStepper.data<string>('uuid');
            const pid = quantityStepper.data<string>('pid');
            const currentQuantity = quantityStepper.currentValue;

            const { lineItemMinOrderQuantity } = quantityStepper.prefs();

            const isQuantityBelowMinValue = lineItemMinOrderQuantity
                ? currentQuantity < lineItemMinOrderQuantity
                : false;

            if ((currentQuantity === 0) || isQuantityBelowMinValue) {
                this.removeProduct(quantityStepper);
                this.hideProgressBar();

                return Promise.resolve(null);
            }

            const requestObject = submitFormJsonWithAbort<TCart, { redirectUrl: string }>(url, {
                pid: pid,
                uuid: uuid,
                quantity: quantityStepper.getValue()
            }, 'POST', false);

            this.updateQtyRequests.push(requestObject);

            return requestObject.promise
                .then((response: Awaited<typeof requestObject.promise>) => {
                    if (!response) {
                        return null; // Aborted request
                    }

                    if (!response.success) {
                        return this.handleFailedQtyUpdateRequest(uuid, response);
                    }

                    this.renderCart(response);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.quantitychanged);

                    return response;
                })
                .catch((error) => {
                    showErrorLayout(error.message);

                    return null;
                })
                .finally(() => {
                    this.hideProgressBar();

                    const index = this.updateQtyRequests.indexOf(requestObject);

                    if (index > -1) {
                        this.updateQtyRequests.splice(index, 1);
                    }
                });
        }

        /**
         * @description Handler for update quantity request response
         * @param uuid product unique identifier
         * @param response - failed response object
         * @returns handled response
         */
        handleFailedQtyUpdateRequest(uuid: string | undefined, response: TFailedRequestResponse & { redirectUrl: string }) {
            if (uuid && response.errorMessage) {
                this.renderCartWithItemLevelActionError(uuid, response.errorMessage);
            }

            if (response.redirectUrl) {
                window.location.assign(response.redirectUrl);
            }

            return null;
        }

        /**
         * @description Aborts redundant update product quantity requests
         */
        dismissQty() {
            this.updateQtyRequests.forEach((request) => {
                request.abortController.abort();
            });

            this.updateQtyRequests = [];
        }

        /**
         * @description Fires `alert.show` event to be handled with Global Alerting System
         * @param accessibilityAlert - alert string
         * @emits "alert.show"
         */
        accessibilityAlert(accessibilityAlert: string) {
            this.eventBus().emit('alert.show', {
                accessibilityAlert
            });
        }

        /**
         * @description On modal window show callback
         * @param editBtn - Target "Edit" button
         * @emits "product.tile.qv.open.edit"
         */
        onShowModal(editBtn: TRefElementInstance) {
            /**
             * @description Global event to show Quick View in edit mode
             * @event "product.tile.qv.open.edit"
             */
            this.eventBus().emit('product.tile.qv.open.edit', editBtn);
        }

        /**
         * @description Handler for "Edit" product button in a Cart
         * @param editBtn - Target "Edit" button
         */
        editProduct(editBtn: TRefElementInstance) {
            this.showProgressBar();

            const uuid = editBtn.data<string>('uuid');
            const editModeQV = editBtn.data<string>('editModeQv') || 'false';
            const isGiftCertificate = editBtn.data<boolean>('giftCertificate') || false;
            const href = editBtn.data<string>('href');
            const wrapperClasses: Array<string> = [];

            wrapperClasses.push(this.prefs().classesModalQuickView);

            this.getById('editProductModal', (editProductModal: TModalInstance) => {
                if (isGiftCertificate) {
                    wrapperClasses.push(this.prefs().classesModalGiftCertificate);
                } else {
                    editProductModal.ref('container').removeClass(this.prefs().classesModalGiftCertificate);
                }

                editProductModal.showModalPreview(wrapperClasses);
            });

            getContentByUrl(href, { uuid, editModeQV })
                .then((response) => {
                    this.getById('editProductModal', (editProductModal: TModalInstance) => editProductModal.showModal({
                        body: response,
                        attributes: {
                            'data-tau-unique': 'edit_product_dialog'
                        }
                    }, this.onShowModal.bind(this, editBtn), true));
                })
                .catch(error => {
                    this.getById('editProductModal', (editProductModal: TModalInstance) => editProductModal.closeModal());
                    this.renderCartWithItemLevelActionError(uuid, error.errorMessage);
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Handler for "Remove" product button in a Cart
         * Stores clicked button instance to property because of "Remove Popup" displaying and further interaction
         * @param removeButton - Clicked "Remove" product button or qtyStepper if value was 0
         */
        removeProduct(removeButton: TRefElementInstance | ICartInputStepperInstance): void {
            const InputStepper = this.getConstructor('inputStepper');
            const url = removeButton.data<string>('removeAction') || removeButton.data<string>('action');
            const pid = removeButton.data<string>('pid');
            const uuid = removeButton.data<string>('uuid');
            const optionId = removeButton.data<string>('optionId');
            const optionValue = removeButton.data<string>('optionValue');
            const customOptionValue = removeButton.data<string>('customOptionValue');
            const skipConfirmation = removeButton.data<string>('skipConfirmation');
            const qtyStepperID = `quantityStepper-${uuid}`;

            const attributes: TAttributes = {
                'data-tau': this.prefs().tauAttrRemoveProductConfirmationPopUp,
                url: url,
                pid: pid,
                uuid: uuid,
                currentValue: removeButton.data<number>('quantity'),
                minOrderQuantity: null
            };

            if (optionId) { attributes.optionId = optionId; }

            if (optionValue) { attributes.optionVal = optionValue; }

            if (customOptionValue) { attributes.customOptionValue = customOptionValue; }

            if (removeButton instanceof InputStepper) {
                attributes.currentValue = parseInt(removeButton.getValue(), 10);

                attributes.minOrderQuantity = removeButton.prefs().lineItemMinOrderQuantity;
            } else {
                this.getById(qtyStepperID, (stepper: TInputStepperInstance) => {
                    attributes.currentValue = parseInt(stepper.getValue(), 10);
                });
            }

            this.removeButton = removeButton;

            if (skipConfirmation) {
                this.confirmedRemoveProduct({ attributes });
            } else {
                this.getById('confirmDialog', (confirmDialog: TConfirmDialogInstance) => {
                    confirmDialog.showModal({
                        productName: removeButton.data<string>('name'),
                        attributes
                    });
                });
            }
        }

        /**
         * @description Renders Cart with error message on Cart Item level.
         * Usually it may happens when in parallel tab user removed product from cart.
         * As far as we should display error on product, which was removed, thus we don't need
         * to update this exact item in a stored earlier cart object
         * @param itemUUID - Product item identifier
         * @param error - an error response from the server
         */
        renderCartWithItemLevelActionError(itemUUID: string | undefined, error: string) {
            const { items, valid } = this.prefs();

            this.setPrefs({
                items: items.map(item => {
                    const existingOldItem = this.prefs().items.find((cartItem) => cartItem.UUID === item.UUID);
                    const returnItem = !existingOldItem ? item : existingOldItem;

                    return {
                        ...returnItem,
                        actionErrorMessage: (itemUUID === item.UUID) ? error : ''
                    };
                }),
                valid: {
                    ...valid,
                    error: true
                }
            });

            this.renderCart(this.prefs(), false);
        }

        /**
         * @description A callback, which is executed, when customer agreed to remove product in confirmation popup
         * @param object - Configuration object or instance of ConfirmDialog
         * @param object.attributes - Product details (based on attributes of confirmation dialog)
         * @param [movedToWishlist] - Indicates if product was moved to wishlist
         * @emits "cart.remove.product"
         * @returns Promise object represents server response for product deletion
         */
        confirmedRemoveProduct({ attributes }: { attributes: TAttributes }, movedToWishlist = false) {
            if (!attributes) {
                return Promise.resolve({ success: false });
            }

            const url = attributes.url;
            const pid = attributes.pid;
            const uuid = attributes.uuid;

            this.eventBus().emit('cart.remove.product', this);

            if (!movedToWishlist) {
                this.showProgressBar();
            }

            let removePromise = submitFormJson(url, {
                pid: pid,
                uuid: uuid
            }, 'POST')
                .then((response: TServerResponse & {basket: unknown}) => {
                    if (!response.success) {
                        throw Error(<string> response.errorMessage);
                    }

                    this.confirmationRemoveCallback(response, movedToWishlist);

                    return response;
                });

            if (!movedToWishlist) {
                removePromise = removePromise
                    .catch(error => {
                        this.renderCartWithItemLevelActionError(uuid, error.message);

                        return error;
                    })
                    .finally(() => {
                        this.hideProgressBar();
                    });
            }

            return removePromise;
        }

        /**
         * @description Handle cancel confirmDialog that was called due quantity stepper value is 0
         * Set quantity stepper value to 1 if confirmDialog cancel
         * @param confirmationDialog Target popup
         */
        cancelProductRemoving(confirmationDialog: TConfirmDialogInstance) {
            const attributes = confirmationDialog && confirmationDialog.attributes;

            if (!attributes) {
                return;
            }

            if (attributes.currentValue === 0 || attributes.currentValue < attributes.minOrderQuantity) {
                const uuid = attributes.uuid;
                const qtyStepperID = `quantityStepper-${uuid}`;

                this.getById(qtyStepperID, (stepper: TInputStepperInstance) => {
                    stepper.setInputValue(stepper.previousValue);
                });
            }
        }

        /**
         * @description Move product from cart to wishlist.
         * In fact this functionality implemented in 2 steps:
         * 1. Add product to wishlist
         * 2. Remove product from cart
         * This is done due to possible backend code duplication in one universal endpoint
         * @param confirmationDialog Target popup
         * @returns Promise object represents server response for product adding to wishlist
         */
        moveProductToWishlist(confirmationDialog: TConfirmDialogInstance): Promise<Array<Record<string, unknown>>> {
            if (!confirmationDialog) {
                return Promise.resolve([{ error: true }]);
            }

            const removeButtonUUID = confirmationDialog.attributes.uuid;

            const productAttributes = this.getProductAttributes(confirmationDialog);

            this.showProgressBar();

            return submitFormJson(this.prefs().actionUrls.moveProductToWishlist, productAttributes)
                .then((response: TServerResponse) => {
                    if (!response.success) {
                        throw Error(<string>response.errorMessage);
                    }

                    const removeFromCartPromise = this.confirmedRemoveProduct(confirmationDialog, true);

                    return Promise.all([response, removeFromCartPromise]);
                })
                .catch((error: Error) => {
                    this.renderCartWithItemLevelActionError(removeButtonUUID, <string>error.message);

                    return [{ error }];
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description A callback, which is executed once `remove` product server call was performed
         * @param cart - server response object
         * @param [movedToWishlist] - Indicates if product was moved to wishlist
         */
        confirmationRemoveCallback(cart, movedToWishlist = false) {
            this.renderCart(cart)
                .then(() => this.accessibilityAlert(movedToWishlist
                    ? this.prefs().accessibilityAlerts.movedtowishlist
                    : this.prefs().accessibilityAlerts.productremoved));
        }

        /**
         * @description Shows progress bar
         */
        showProgressBar() {
            this.ref('cartContainer').attr('aria-busy', 'true');
        }

        /**
         * @description Hides progress bar
         */
        hideProgressBar() {
            this.ref('cartContainer').attr('aria-busy', 'false');
        }

        /**
         * @description Handles `proceed to checkout` button click.
         *
         * In case of error - to not allow Customer to enter checkout flow and scroll to cart error message
         *
         * @param btn - clicked button
         * @param event - target event
         * @emits "cart.page.submitted"
         */
        proceedToCheckout(btn: TRefElementInstance, event: Event) {
            if (btn.data('error')) {
                event.preventDefault();
                this.scrollToError();
            }

            const isMobile = !isDesktopView();
            let ctaFeature: string | undefined;

            if (isMobile) {
                ctaFeature = event?.isTrusted ? 'cta_fixed' : 'cta_sticky';
            }

            this.eventBus().emit('cart.page.submitted', this, ctaFeature);
        }

        /**
         * @description Scrolls cart error messages into viewport
         */
        scrollToError() {
            const cartErrorMessage = this.ref('errorMsgs').get();

            if (cartErrorMessage) {
                scrollWindowTo(cartErrorMessage);
            }
        }

        /**
         * @description Show cart error
         * @param widget - Widget that emit error
         * @param error - Error message string
         */
        showError(widget: TWidget, error: string) {
            if (!error) {
                return;
            }

            this.setPref('pageError', error);

            this.renderCart(this.prefs())
                .then(() => this.scrollToError());
        }

        getProductAttributes(confirmationDialog: TConfirmDialogInstance): TProductAttributes {
            const productAttributes: TProductAttributes = {
                pid: confirmationDialog.attributes.pid,
                qty: confirmationDialog.attributes.currentValue
            };

            if (confirmationDialog.attributes.optionId) {
                productAttributes.optionId = confirmationDialog.attributes.optionId;
            }

            if (confirmationDialog.attributes.optionVal) {
                productAttributes.optionVal = confirmationDialog.attributes.optionVal;
            }

            if (confirmationDialog.attributes.customOptionValue) {
                productAttributes.customOptionValue = confirmationDialog.attributes.customOptionValue;
            }

            return productAttributes;
        }

        /**
         * @description Select shipping method handler
         * @param radioSelector - Shipping method`s input
         * @listens RadioSelector#change
         * @emits "cart.update.shipping.method"
         * @returns Promise object represents server response for shipping method selection
         */
        selectShippingMethodOnCart(radioSelector: TRadioSelectorInstance): ReturnType<typeof submitFormJson> {
            const newShippingMethodID = radioSelector.getDataValue();

            if (this.prefs().shipments[0].selectedShippingMethod === newShippingMethodID) {
                return Promise.resolve();
            }

            return submitFormJson(
                this.prefs().actionUrls.selectShippingMethod,
                { methodID: newShippingMethodID }
            ).then((response) => {
                if (!response || !response.success) {
                    return Promise.reject(response.errorMessage);
                }

                this.eventBus().emit(
                    'cart.update.shipping.method',
                    response.order
                );

                return this.renderCart(response.cart);
            }).catch((error: string) => {
                this.showShippingMethodError(radioSelector, error);
            });
        }

        /**
         * @description Display shipping method error with the passed error message
         * After changing shipping method, the error will be hidden again
         *
         * @param radioSelector shipping method selector instance
         * @param error error message
         */
        showShippingMethodError(radioSelector: TRadioSelectorInstance, error: string) {
            radioSelector.ref('errorFeedback').setText(error).attr('hidden', false);
            radioSelector.setValue(this.prefs().shipments[0].selectedShippingMethod);
        }
    }

    return CartMgr;
}

export type TCartMgr = ReturnType<typeof CartMgrClassCreator>;

export type TCartMgrInstance = InstanceType<TCartMgr>;

export default CartMgrClassCreator;
